import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatOptionModule } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { ActivatedRoute } from "@angular/router";
import { LoadingDialogComponent } from "@cotecna/dialogs";
import { DirectivesModule, InfiniteScrollDirective } from "@cotecna/directives";
import { TableColumn, TablesModule } from "@cotecna/tables";
import { BehaviorSubject, map, Observable } from "rxjs";
import {
  InspectionAuditActions,
  InspectionAuditActionsApi,
} from "src/app/inspection/model/event-history";
import { AuditService } from "src/app/inspection/services/audit-service";

import { DropdownType } from "./enums/dropdown-type";
import {
  DATA_INPUT_TABLE_COLUMNS_GENERAL_INFO,
  DATA_INPUT_TABLE_COLUMNS_ITEMS,
  DATA_INPUT_TABLE_COLUMNS_SCOPE_OF_WORK,
} from "./models/data-input-table-columns";
import { DataInputTableRow } from "./models/data-input-table-row";
import { GeneralInfoData } from "./models/general-info";
import { ItemData } from "./models/item";
import { ScopeOfWorkData } from "./models/scope-of-work";

@Component({
  selector: "im-data-input-log",
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatOptionModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    TablesModule,
    DirectivesModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./data-input-log.component.html",
  styleUrl: "./data-input-log.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataInputLogComponent {
  @ViewChild(InfiniteScrollDirective)
  public infiniteScroll: InfiniteScrollDirective;

  public DropdownType = DropdownType;
  public numberOfDropdowns: number = 1;
  public typeOfDropdown: DropdownType = DropdownType.GeneralInfo;
  public entryTypes: { value: number; label: string }[] = [
    { value: 1, label: "General Info" },
    { value: 2, label: "Items" },
    { value: 3, label: "Scope of Work" },
  ];
  public entryType: number | null = null;
  public items: { value: string; label: string }[] = [];
  public item: string = "";
  public sections: { value: string; label: string }[] = [];
  public section: string = "";
  public inputFields: { value: string; label: string }[] = [];
  public inputField: string = "";

  public isLoading: boolean = false;
  public generalInfoColumns: TableColumn[] =
    DATA_INPUT_TABLE_COLUMNS_GENERAL_INFO;
  public itemsColumns: TableColumn[] = DATA_INPUT_TABLE_COLUMNS_ITEMS;
  public scopeOfWorkColumns: TableColumn[] =
    DATA_INPUT_TABLE_COLUMNS_SCOPE_OF_WORK;
  public rawData$: Observable<DataInputTableRow[]>;

  private inspectionId!: string;
  private pageNumber: number = 0;
  private pageSize: number = 20;
  private tableData$: EntryType[] = [];
  private valueChange: BehaviorSubject<EntryType[]> = new BehaviorSubject<
    GeneralInfoData[]
  >([]);

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly auditService = inject(AuditService);
  private readonly dialog = inject(MatDialog);

  public ngOnInit(): void {
    this.getInspectionIdFromRoute();
    this.rawData$ = this.valueChange.pipe(
      map((valueChange) => this.mapToTableData(valueChange)),
    );
  }

  public handleDropdownChange(
    dropdownValue: number,
    dropdownNumber: number,
  ): void {
    this.resetFormState();
    switch (dropdownNumber) {
      case 1:
        this.handleEntryTypeSelection(dropdownValue);
        break;
      case 2:
        this.resetSection();
        this.resetInputField();
        this.loadNextPage();
        this.numberOfDropdowns = 3;
        this.getItemSections();
        break;
      case 3:
        switch (this.typeOfDropdown) {
          case DropdownType.Items:
            this.resetInputField();
            this.loadNextPage();
            this.numberOfDropdowns = 4;
            this.getItemInputFields();
            break;
          case DropdownType.ScopeOfWork:
            this.numberOfDropdowns = 3;
            this.resetInputField();
            this.loadNextPage();
            this.getScopeOfWorkInputFields();
            break;
        }
        break;
      case 4:
        this.loadNextPage();
        break;
    }
  }

  public downloadDocument(): void {
    const loadingDialog = this.dialog.open(LoadingDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: "Downloading Document",
    });
    switch (this.typeOfDropdown) {
      case DropdownType.GeneralInfo:
        this.auditService
          .downloadGeneralInfoDocument(this.inspectionId)
          .subscribe({
            error: () => loadingDialog.close(),
            complete: () => loadingDialog.close(),
          });
        break;
      case DropdownType.Items:
        this.auditService
          .downloadItemsDocument(
            this.inspectionId,
            this.item,
            this.section,
            this.inputField,
          )
          .subscribe({
            error: () => loadingDialog.close(),
            complete: () => loadingDialog.close(),
          });
        break;
      case DropdownType.ScopeOfWork:
        this.auditService
          .downloadScopeOfWorkDocument(
            this.inspectionId,
            this.section,
            this.inputField,
          )
          .subscribe({
            error: () => loadingDialog.close(),
            complete: () => loadingDialog.close(),
          });
        break;
    }
  }

  public clearFormState(): void {
    this.numberOfDropdowns = 1;
    this.entryType = null;
    this.item = "";
    this.section = "";
    this.inputField = "";
    this.resetFormState();
  }

  public loadNextPage() {
    if (this.tableData$.length == 0 || this.tableData$.length % 20 === 0) {
      switch (this.typeOfDropdown) {
        case DropdownType.GeneralInfo:
          this.getGeneralInfoData();
          break;
        case DropdownType.Items:
          this.getItemsData();
          break;
        case DropdownType.ScopeOfWork:
          this.getScopeOfWorkData();
          break;
      }
      this.pageNumber++;
    }
  }

  public getTableInteractionClass(): string {
    return this.isLoading ? "interactions-disabled" : null;
  }

  private handleEntryTypeSelection(dropdownValue: number) {
    this.resetItem();
    this.resetSection();
    this.resetInputField();
    switch (dropdownValue) {
      case 1:
        this.typeOfDropdown = DropdownType.GeneralInfo;
        this.numberOfDropdowns = 1;
        this.loadNextPage();
        break;
      case 2:
        this.typeOfDropdown = DropdownType.Items;
        this.numberOfDropdowns = 2;
        this.loadNextPage();
        this.getItems();
        break;
      case 3:
        this.typeOfDropdown = DropdownType.ScopeOfWork;
        this.numberOfDropdowns = 2;
        this.loadNextPage();
        this.getScopeOfWorkSections();
        break;
    }
  }

  private getInspectionIdFromRoute(): void {
    this.activatedRoute.parent?.params.subscribe((params) => {
      this.inspectionId = params["id"];
    });
  }

  private getGeneralInfoData(): void {
    this.isLoading = true;
    this.auditService
      .getGeneralInfoData(this.inspectionId, this.pageNumber, this.pageSize)
      .subscribe({
        next: (response) => {
          this.tableData$ = [...this.tableData$, ...response];
          this.valueChange.next(this.tableData$);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private getItemsData(): void {
    if (this.item !== "") {
      this.isLoading = true;
      this.auditService
        .getItemsData(
          this.inspectionId,
          this.item,
          this.section,
          this.inputField,
          this.pageNumber,
          this.pageSize,
        )
        .subscribe({
          next: (response) => {
            this.tableData$ = [...this.tableData$, ...response];
            this.valueChange.next(this.tableData$);
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
        });
    }
  }

  private getScopeOfWorkData(): void {
    this.isLoading = true;
    this.auditService
      .getScopeOfWorkData(
        this.inspectionId,
        this.section,
        this.inputField,
        this.pageNumber,
        this.pageSize,
      )
      .subscribe({
        next: (response) => {
          this.tableData$ = [...this.tableData$, ...response];
          this.valueChange.next(this.tableData$);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private getItems(): void {
    this.auditService.getItems(this.inspectionId).subscribe((items) => {
      this.items = items.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    });
  }

  private getItemSections(): void {
    this.auditService
      .getItemSections(this.inspectionId, this.item)
      .subscribe((sections) => {
        this.sections = sections.map((section) => ({
          value: section.sectionId,
          label: section.sectionName,
        }));
      });
  }

  private getScopeOfWorkSections(): void {
    this.auditService
      .getScopeOfWorkSections(this.inspectionId)
      .subscribe((sections) => {
        this.sections = sections.map((section) => ({
          value: section.sectionId,
          label: section.sectionName,
        }));
      });
  }

  private getItemInputFields(): void {
    this.auditService
      .getItemsInputFields(this.inspectionId, this.item, this.section)
      .subscribe((inputFieldNames) => {
        this.inputFields = inputFieldNames.map((inputFieldNames) => ({
          value: inputFieldNames.fieldId,
          label: inputFieldNames.fieldName,
        }));
      });
  }

  private getScopeOfWorkInputFields(): void {
    this.auditService
      .getScopeOfWorkInputFields(this.inspectionId, this.section)
      .subscribe((inputFieldNames) => {
        this.inputFields = inputFieldNames.map((inputFieldNames) => ({
          value: inputFieldNames.fieldId,
          label: inputFieldNames.fieldName,
        }));
      });
  }

  private mapToTableData(valueChange: EntryType[]): DataInputTableRow[] {
    const data: DataInputTableRow[] = [];

    switch (this.typeOfDropdown) {
      case DropdownType.GeneralInfo:
        (valueChange as GeneralInfoData[]).forEach((value) => {
          data.push({
            dateTime: value.eventDate,
            author: value.userName,
            itemName: "",
            section: this.resolveSectionLabelFromAction(value.action),
            subSection: "",
            fieldName: value.metadata[0].value,
            newValue: value.metadata[1].value,
          });
        });
        break;
      case DropdownType.Items:
        (valueChange as ItemData[]).forEach((value) => {
          data.push({
            dateTime: value.date,
            author: value.author,
            itemName: value.itemName,
            section: value.sectionName,
            subSection: value.subSectionName,
            fieldName: value.fieldName,
            newValue: value.value,
          });
        });
        break;
      case DropdownType.ScopeOfWork:
        (valueChange as ScopeOfWorkData[]).forEach((value) => {
          data.push({
            dateTime: value.date,
            author: value.author,
            itemName: "",
            section: value.sectionName,
            subSection: value.subSectionName,
            fieldName: value.fieldName,
            newValue: value.value,
          });
        });
        break;
    }

    return data;
  }

  private resolveSectionLabelFromAction(
    action: number,
  ): InspectionAuditActions {
    switch (action) {
      case InspectionAuditActionsApi.GeneralInfoDetails_FieldChange:
        return InspectionAuditActions.GeneralInfoDetails;
      case InspectionAuditActionsApi.GeneralInfoDetails_Creation:
        return InspectionAuditActions.GeneralInfoDetails;
      case InspectionAuditActionsApi.GeneralInfoAdditionalFields_FieldChangeValue:
        return InspectionAuditActions.GeneralInfoAdditionalFields_FieldChangeValue;
    }
  }

  private resetFormState(): void {
    this.pageNumber = 0;
    this.tableData$ = [];
    this.valueChange.next([]);
    this.infiniteScroll?.resetMaxScrollOffset();
  }

  private resetItem(): void {
    this.item = "";
  }

  private resetSection(): void {
    this.section = "";
  }

  private resetInputField(): void {
    this.inputField = "";
  }
}

type EntryType = GeneralInfoData | ItemData | ScopeOfWorkData;
